import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/types/movie";
import RatingBadge from "@/components/base/RatingBadge";

interface HeroCarouselProps {
  items: Movie[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 5200);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) {
    return (
      <div className="flex h-[380px] items-center justify-center rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-500 md:h-[460px]">
        暂无相关资源，敬请期待
      </div>
    );
  }

  const go = (dir: number) => {
    setIndex((prev) => (prev + dir + items.length) % items.length);
  };

  const active = items[index];

  return (
    <div
      className="group relative h-[380px] overflow-hidden rounded-lg border border-background-200 bg-primary-950 md:h-[460px]"
      onTouchStart={(e) => {
        touchStartX.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        if (touchStartX.current === null) return;
        const delta = e.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(delta) > 45) go(delta < 0 ? 1 : -1);
        touchStartX.current = null;
      }}
    >
      {items.map((movie, i) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
          aria-hidden={i !== index}
        >
          <img
            src={movie.banner || movie.cover}
            alt={`${movie.title} 剧照`}
            title={`${movie.title} - 热播推荐`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/95 via-primary-950/55 to-primary-950/10" />
        </div>
      ))}

      <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-md bg-accent-500 px-2 py-0.5 text-xs font-bold text-primary-950">
            热播推荐
          </span>
          <span className="rounded-md bg-background-50/15 px-2 py-0.5 text-xs font-medium text-background-100 backdrop-blur">
            {active.type}
          </span>
          <span className="text-xs text-background-200">{active.year} · {active.region}</span>
        </div>
        <h2 className="mt-3 font-heading text-2xl font-bold text-background-50 md:text-3xl">
          {active.title}
        </h2>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <RatingBadge rating={active.rating} size="md" />
          <span className="text-xs text-background-200">{active.genres.join(" / ")}</span>
        </div>
        <p className="mt-3 hidden max-w-xl text-sm leading-relaxed text-background-200 sm:block">
          {active.synopsis}
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Link
            to={`/detail/${active.id}`}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-information-line text-base" aria-hidden="true" />
            查看详情
          </Link>
          <Link
            to={`/category/${active.type}`}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-50/40 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
          >
            更多{active.type}
          </Link>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(-1)}
        aria-label="上一张"
        className="absolute left-3 top-1/2 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-primary-950/50 text-background-50 opacity-0 transition-opacity hover:bg-primary-950/80 group-hover:opacity-100 md:flex"
      >
        <i className="ri-arrow-left-s-line text-xl" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        aria-label="下一张"
        className="absolute right-3 top-1/2 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-primary-950/50 text-background-50 opacity-0 transition-opacity hover:bg-primary-950/80 group-hover:opacity-100 md:flex"
      >
        <i className="ri-arrow-right-s-line text-xl" aria-hidden="true" />
      </button>

      <div className="absolute bottom-3 right-6 flex items-center gap-1.5 md:bottom-6">
        {items.map((movie, i) => (
          <button
            key={movie.id}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`切换到 ${movie.title}`}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? "w-6 bg-accent-500" : "w-1.5 bg-background-50/50"
            }`}
          />
        ))}
      </div>
    </div>
  );
}